import { useState, useEffect } from 'react';
import { useSiteContext } from '../contexts/SiteContext';
import { AlertCircle, CheckCircle, Users, UserCheck, UserPlus, Mail, Shield, Loader, TrendingUp } from 'lucide-react';

const UserAnalysis = ({ user }) => {
  const { selectedSite } = useSiteContext();
  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (selectedSite) {
      fetchUserData();
    } else {
      setLoading(false);
      setError('Please select a site');
    }
  }, [user, selectedSite]);

  const fetchUserData = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `/app/api/analytics/user-analysis.php?siteId=${selectedSite.id}`,
        {
          headers: {
            'Authorization': `Bearer ${user.token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const result = await response.json();

      if (response.ok && result.success) {
        setUserData(result.data);
      } else {
        setError(result.message || 'Failed to fetch user data');
      }
    } catch (error) {
      console.error('Error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <Loader className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading user analysis...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="rounded-lg bg-red-50 p-4 flex items-center gap-3">
          <AlertCircle className="h-5 w-5 text-red-600" />
          <p className="text-red-800">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Users className="h-8 w-8 text-blue-600" />
            User Analysis
          </h1>
          <p className="text-gray-600 mt-2">Customer accounts, admin users, and user engagement metrics</p>
        </div>

        {/* Summary Stats - Only show if multiple sites */}
        {userData.length > 1 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600">Total Customers</p>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {userData.reduce((sum, d) => sum + (d.total_customers || 0), 0)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600">Paying Customers</p>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {userData.reduce((sum, d) => sum + (d.customers_with_orders || 0), 0)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600">Active (90d)</p>
              <p className="text-3xl font-bold text-purple-600 mt-2">
                {userData.reduce((sum, d) => sum + (d.active_customers || 0), 0)}
              </p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-600">New (30d)</p>
              <p className="text-3xl font-bold text-orange-600 mt-2">
                {userData.reduce((sum, d) => sum + (d.new_customers_30d || 0), 0)}
              </p>
            </div>
          </div>
        )}

        {/* Detailed Data */}
        <div className="space-y-6">
          {userData.map((site) => (
            <div key={site.site_id} className="bg-white rounded-lg shadow overflow-hidden">
              <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Users className="h-6 w-6 text-blue-600" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{site.site_name}</h3>
                    </div>
                  </div>
                  {site.error ? (
                    <AlertCircle className="h-5 w-5 text-red-500" />
                  ) : (
                    <CheckCircle className="h-5 w-5 text-green-500" />
                  )}
                </div>
              </div>

              <div className="p-6">
                {site.error ? (
                  <div className="rounded-lg bg-red-50 p-4 flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-red-900">Error</p>
                      <p className="text-sm text-red-700">{site.error}</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Customer Statistics */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Customer Statistics
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center gap-2 mb-1">
                            <Users className="h-4 w-4 text-blue-600" />
                            <p className="text-xs text-blue-600 uppercase font-medium">Total Customers</p>
                          </div>
                          <p className="text-2xl font-bold text-blue-900">{site.total_customers}</p>
                        </div>
                        <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                          <div className="flex items-center gap-2 mb-1">
                            <UserCheck className="h-4 w-4 text-green-600" />
                            <p className="text-xs text-green-600 uppercase font-medium">Paying Customers</p>
                          </div>
                          <p className="text-2xl font-bold text-green-900">{site.customers_with_orders}</p>
                          {site.total_customers > 0 && (
                            <p className="text-xs text-green-700 mt-1">
                              {((site.customers_with_orders / site.total_customers) * 100).toFixed(1)}% conversion
                            </p>
                          )}
                        </div>
                        <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                          <div className="flex items-center gap-2 mb-1">
                            <TrendingUp className="h-4 w-4 text-purple-600" />
                            <p className="text-xs text-purple-600 uppercase font-medium">Active (90d)</p>
                          </div>
                          <p className="text-2xl font-bold text-purple-900">{site.active_customers}</p>
                          {site.total_customers > 0 && (
                            <p className="text-xs text-purple-700 mt-1">
                              {((site.active_customers / site.total_customers) * 100).toFixed(1)}% active
                            </p>
                          )}
                        </div>
                        <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                          <div className="flex items-center gap-2 mb-1">
                            <UserPlus className="h-4 w-4 text-orange-600" />
                            <p className="text-xs text-orange-600 uppercase font-medium">New (30d)</p>
                          </div>
                          <p className="text-2xl font-bold text-orange-900">{site.new_customers_30d}</p>
                        </div>
                      </div>
                    </div>

                    {/* Newsletter Subscribers */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Newsletter Subscribers
                      </h4>
                      <div className="p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xs text-indigo-600 uppercase font-medium mb-1">Total Subscribers</p>
                            <p className="text-2xl font-bold text-indigo-900">{site.newsletter_subscribers}</p>
                          </div>
                          <Mail className="h-8 w-8 text-indigo-600" />
                        </div>
                        {site.total_customers > 0 && (
                          <p className="text-xs text-indigo-700 mt-2">
                            {((site.newsletter_subscribers / site.total_customers) * 100).toFixed(1)}% of customers subscribed
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Customer Groups */}
                    {Array.isArray(site.customer_groups) && site.customer_groups.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Customer Groups
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                          {site.customer_groups.map((group) => (
                            <div key={group.id} className="p-3 bg-gray-50 rounded border border-gray-200">
                              <div className="flex items-center justify-between mb-1">
                                <span className="text-sm font-medium text-gray-900">{group.code}</span>
                                <span className="text-xs text-gray-500">ID: {group.id}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Users className="h-3 w-3 text-blue-600" />
                                <span className="text-xs font-semibold text-blue-600">
                                  {group.customer_count || 0} customers
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Admin Users */}
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Shield className="h-4 w-4" />
                        Admin Users
                      </h4>
                      {/*site.admin_users_note ? (
                        <div className="p-4 bg-yellow-50 rounded border border-yellow-200">
                          <div className="flex items-start gap-2">
                            <AlertCircle className="h-4 w-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-yellow-900">Admin Access Restricted</p>
                              <p className="text-xs text-yellow-700 mt-1">{site.admin_users_note}</p>
                            </div>
                          </div>
                        </div>
                      ) : Array.isArray(site.admin_users) && site.admin_users.length > 0 ? (
                        <div className="space-y-2">
                          <div className="p-3 bg-red-50 rounded border border-red-200 mb-3">
                            <p className="text-sm font-semibold text-red-900">
                              Total Admin Users: {site.total_admins}
                            </p>
                          </div>
                          <div className="grid grid-cols-1 gap-2">
                            {site.admin_users.map((admin) => (
                              <div key={admin.id} className="p-3 bg-gray-50 rounded border border-gray-200">
                                <div className="flex items-center justify-between">
                                  <div>
                                    <p className="text-sm font-medium text-gray-900">
                                      {admin.firstname} {admin.lastname}
                                    </p>
                                    <p className="text-xs text-gray-600">@{admin.username}</p>
                                    <p className="text-xs text-gray-500">{admin.email}</p>
                                  </div>
                                  {admin.is_active ? (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Active
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                      Inactive
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="p-4 bg-gray-50 rounded border border-gray-200 text-center">
                          <p className="text-sm text-gray-600">No admin user data available</p>
                        </div>
                      )*/}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Insights */}
        <div className="mt-8 bg-blue-50 rounded-lg p-6 border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-900 mb-3">User Insights</h3>
          <ul className="text-sm text-blue-800 space-y-2">
            <li>• <strong>Paying Customers:</strong> Customers who have placed at least one order</li>
            <li>• <strong>Active Customers:</strong> Customers who logged in or placed an order within the last 90 days</li>
            <li>• <strong>New Customers:</strong> Accounts created within the last 30 days</li>
            <li>• <strong>Customer Groups:</strong> Used for pricing, promotions, and tax classes. Count shows active customers per group.</li>
            <li>• <strong>Admin Users:</strong> Magento REST API typically restricts admin user endpoints for security. Admin user list is only accessible with special admin-level authentication tokens or direct database access.</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default UserAnalysis;
